import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { connect } from 'react-redux';
import { eventTracker } from '_helpers';
import WWTK from './WWTK';
import Blogs from './Blogs';
import SpotlightMaterial from './SpotlightMaterial/SpotlightMaterial';
import RcForKids from './RcForKids/RcForKids';
import RecycleFundamentals from './RecycleFundamentals';
import RecycleRIGHTGame from './RecycleRIGHTGame';
import events from './events';
import { DownloadAppBanner } from '../../components';
import LearningModules from './LearningModules';
import { checkEdgeCaseRecycleRIGHT } from './helpers';
import CustomEducationComponent from './CustomEducationComponent/CustomEducationComponent';
import FeaturedLessons from './FeaturedLessons';
import { webCampaignBaseUrl } from '../../app/apis';
import isCanadaWebAppHandler from '../../helpers/isCanadaWebAppHandler';
import MisunderstoodMaterial from './MisunderstoodMaterial/MisunderstoodMaterial';

const OpacityAnimation = keyframes`
  from {opacity: 0;}
  to {opacity: 1;}
`;

const Container = styled.div`
  animation: ${OpacityAnimation} 0.5s;
  padding: 10px;
  * {
    box-sizing: border-box;
  }
`;

const LMContainer = styled.div`
  background-color: white;
  border-radius: 10px;
  box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 18px 0px;
`;

const Education = ({
  wwtkItems,
  blogs,
  spotlight_materials,
  rf_lessons,
  districtId,
  projectId,
  uuid,
  selectedLang,
  zones,
  recycleRightData,
  learning_modules,
  hasDzCustomConfig,
  dzCustomConfig,
  featured_lessons,
  has_app_download,
  misunderstood_materials
}) => {
  const blogsRef = useRef();
  const SPRef = useRef();
  const FLRef = useRef();
  const RFRef = useRef();
  const learningModulesRef = useRef();
  const MMRef = useRef();

  const [ref, setRef] = useState();
  const [windowsWidth, setWindowsWidth] = useState(document.getElementById('rcroot').getBoundingClientRect().width);
  const [displayedContent, setDisplayedContent] = useState();
  const [displayedLMDetails, setDisplayedLMDetails] = useState('1');

  const isOpenHandler = (name) => !displayedContent || displayedContent === name;
  const isDataValid = (data) => data?.length !== 0 && Object.values(data).length > 0;

  // ignore stage value if has_dz_custom_config true
  const validate = (name) => {
    switch (name) {
      case 'wwtk':
        return (hasDzCustomConfig ? dzCustomConfig?.has_wwtk : true)
          && !displayedContent && wwtkItems.length > 0;
      case 'featuredLessons':
        return isOpenHandler('featuredLessons') && (!hasDzCustomConfig || dzCustomConfig?.has_featured_lesson) && Object.keys(featured_lessons).length > 0;
      case 'learningModules':
        return isOpenHandler('learningModules') && (!hasDzCustomConfig || dzCustomConfig?.has_learning_module) && isDataValid(learning_modules);
      case 'rfLessons':
        return isOpenHandler('rfLessons') && (!hasDzCustomConfig || dzCustomConfig?.has_rf_lesson) && isDataValid(rf_lessons);
      case 'recycleRIGHT':
        return isOpenHandler('recycleRIGHT') && (!hasDzCustomConfig || dzCustomConfig?.has_recycle_right_game) && checkEdgeCaseRecycleRIGHT(recycleRightData);
      case 'spotlightMaterials':
        return isOpenHandler('spotlightMaterial') && (!hasDzCustomConfig || dzCustomConfig?.has_material_spotlight) && isDataValid(spotlight_materials);
      case 'rcForKids':
        return !displayedContent && (!hasDzCustomConfig || dzCustomConfig?.has_kids_package);
      case 'blogs':
        return isOpenHandler('blog') && (!hasDzCustomConfig || dzCustomConfig?.has_blogs) && isDataValid(blogs);
      case 'misunderstoodMaterials':
        return isOpenHandler('misunderstoodMaterial') && (!hasDzCustomConfig || dzCustomConfig?.has_misunderstood_materials) && isDataValid(misunderstood_materials);
      default:
        return false;
    }
  };

  useEffect(() => {
    const updateWindowDimensions = () => {
      setWindowsWidth(document.getElementById('rcroot').getBoundingClientRect().width);
    };
    window.addEventListener('resize', updateWindowDimensions);
    return () => window.removeEventListener('resize', updateWindowDimensions);
  }, []);

  useEffect(() => {
    if (!displayedContent) {
      if (ref === 'blogsRef') blogsRef.current.scrollIntoView();
      if (ref === 'RFRef') RFRef.current.scrollIntoView();
      if (ref === 'SPRef') SPRef.current.scrollIntoView();
      if (ref === 'FLRef') FLRef.current.scrollIntoView();
      if (ref === 'learningModulesRef') learningModulesRef.current.scrollIntoView();
      if (ref === 'MMRef') MMRef.current.scrollIntoView();
    }
  }, [displayedContent]);

  useEffect(() => {
    eventTracker(events.DZ_HOME_LAND, '');
  }, []);

  const apiGWPrefix = isCanadaWebAppHandler() ? 'ca' : 'us';

  const campaignUrl = `${webCampaignBaseUrl}/?projectId=${projectId}&districtId=${districtId}&uuid=${uuid}&lang=${selectedLang}&zones=${zones}&runson=webapp&apiGWPrefix=${apiGWPrefix}`;

  return (
    <Container windowsWidth={windowsWidth}>

      {validate('wwtk') && <WWTK />}

      {validate('featuredLessons') && (
        <div ref={FLRef}>
          <FeaturedLessons
            setDisplayedContent={setDisplayedContent}
            setRef={setRef}
            campaignUrl={campaignUrl}
            featured_lessons={featured_lessons}
          />
        </div>
      )}

      {validate('learningModules') && (
        <LMContainer ref={learningModulesRef}>
          {learning_modules.map((item, index) => (!displayedContent
            || Number(displayedLMDetails) === index) && (
              <LearningModules
                moduleIndex={index}
                setDisplayedLMDetails={setDisplayedLMDetails}
                learning_module={item}
                windowsWidth={windowsWidth}
                setDisplayedContent={setDisplayedContent}
                setRef={setRef}
              />
            ))}
        </LMContainer>
      )}

      {validate('rfLessons') && (
        <div ref={RFRef}>
          <RecycleFundamentals
            windowsWidth={windowsWidth}
            setDisplayedContent={setDisplayedContent}
            setRef={setRef}
          />
        </div>
      )}

      {validate('misunderstoodMaterials') && (
        <div ref={MMRef}>
          <MisunderstoodMaterial
            windowsWidth={windowsWidth}
            setDisplayedContent={setDisplayedContent}
            setRef={setRef}
          />
        </div>
      )}

      {validate('recycleRIGHT') && (<RecycleRIGHTGame windowsWidth={windowsWidth} setDisplayedContent={setDisplayedContent} />)}

      {validate('spotlightMaterials') && (
        <div ref={SPRef}>
          <SpotlightMaterial
            windowsWidth={windowsWidth}
            setDisplayedContent={setDisplayedContent}
            setRef={setRef}
          />
        </div>
      )}

      {validate('rcForKids') && <RcForKids windowsWidth={windowsWidth} />}

      {dzCustomConfig?.dz_custom_components?.length > 0 && !displayedContent
        && dzCustomConfig?.dz_custom_components.map((item) => (
          <CustomEducationComponent {...item} />
        ))}

      {validate('blogs') && (
        <div ref={blogsRef}>
          <Blogs
            windowsWidth={windowsWidth}
            setDisplayedContent={setDisplayedContent}
            setRef={setRef}
          />
        </div>
      )}

      {!displayedContent && has_app_download && <DownloadAppBanner />}
    </Container>
  );
};
export default connect(
  (store) => ({
    zones: store.zone.zoneStr,
    projectId: store.plugin.projectId,
    uuid: store.plugin.uuid,
    districtId: store.config.districtId,
    countryCode: store.config.countryCode,
    wwtkItems: store.education.wwtk_items,
    featured_lessons: store.education.featured_lessons,
    blogs: store.education.blogs,
    rf_lessons: store.education.rf_lessons,
    learning_modules: store.education.learning_modules,
    spotlight_materials: store.education.spotlight_materials,
    recycleRightData: store.education.recycle_right,
    hasDzCustomConfig: store.config?.webappConfig?.has_dz_custom_config || false,
    dzCustomConfig: store.config?.webappConfig?.dz_custom_config || {},
    has_app_download: store?.config?.webappConfig?.has_app_download,
    misunderstood_materials: store.education.misunderstood_materials
  })
)(Education);
